import React from "react";
import { Input } from "./Input";
import { getLocalTimeFromUtc, getUtcTimeFromLocal } from "../../../../utils/dates";

/** The in-event selling window an entity (client or concession) holds, as a UTC time of day. */
export interface InEventWindowValues {
  in_event_starts_at: string | null;
  in_event_ends_at: string | null;
}

export interface InEventWindowFieldsProps {
  disabled: boolean;
  window: InEventWindowValues;
  emptyValueHint?: string;
  onChange: (window: Partial<InEventWindowValues>) => void;
}

/**
 * Returns what is wrong with the window, mirroring the API rules so the problem shows before saving.
 * Both ends empty is valid and means the entity inherits the window above it. A start later than the end
 * is valid too: it is a window that crosses midnight, such as 23:00 to 01:00.
 */
export const getInEventWindowError = (window: InEventWindowValues): string => {
  const startsAt: string = window.in_event_starts_at ?? "";
  const endsAt: string = window.in_event_ends_at ?? "";
  if (startsAt === "" && endsAt === "") {
    return "";
  }

  if (startsAt === "" || endsAt === "") {
    return "Captura la hora de inicio y de fin de la ventana En evento";
  }

  return startsAt === endsAt
    ? "El inicio y el fin no pueden ser la misma hora"
    : "";
};

/**
 * Hours during which the entity's service zones in "En evento" mode accept app orders. Configured once and
 * applied to the date of every event that has app ordering enabled. Left empty, a concession inherits the
 * client's hours and a client defers to the event's own start and end. It does not move the pre-order
 * cutoff, which the service zone measures from the event's registered start.
 */
export const InEventWindowFields: React.FC<InEventWindowFieldsProps> = (props) => {
  const { disabled, window, emptyValueHint, onChange } = props;
  const error: string = getInEventWindowError(window);

  const handleChange = (field: keyof InEventWindowValues, localTime: string): void => {
    onChange({ [field]: localTime === "" ? null : getUtcTimeFromLocal(localTime) });
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-semibold text-gray-900">
        Horario del modo En evento
      </h3>
      <p className="text-xs text-gray-500">
        Aplica a cada evento con pedidos en app habilitados; no altera el corte
        de preórdenes.{emptyValueHint ? ` ${emptyValueHint}` : ""}
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          disabled={disabled}
          label="Inicio de venta"
          className="appearance-none"
          value={getLocalTimeFromUtc(window.in_event_starts_at ?? "")}
          onChange={(value: string) => handleChange("in_event_starts_at", value)}
          placeholder="Inicio de venta"
          type="time"
          error={error}
          isCopyable={disabled}
        />
        <Input
          disabled={disabled}
          label="Fin de venta"
          className="appearance-none"
          value={getLocalTimeFromUtc(window.in_event_ends_at ?? "")}
          onChange={(value: string) => handleChange("in_event_ends_at", value)}
          placeholder="Fin de venta"
          type="time"
          isCopyable={disabled}
        />
      </div>
    </div>
  );
};
